import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Checkbox } from '@mui/material';

/**
 * TitleDistribution Component
 * 
 * Displays a horizontal bar chart showing different job titles and their percentages
 * based on the titleKeys data. Allows users to select/deselect titles by clicking on them
 * and confirm their selection or choose to specify titles directly.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.titleKeys - Array of title keys sorted by profile count
 * @param {Function} props.onConfirm - Callback when user confirms the titles
 * @param {Function} props.onReject - Callback when user rejects the titles
 * @param {Function} props.advanceToNextTopic - Function to advance to the next topic
 * @param {Function} props.setMessages - Function to update messages state
 * @param {Function} props.setTitleKeys - Function to reset title keys (optional)
 * @returns {JSX.Element} The title distribution component
 */
const TitleDistribution = ({ 
  titleKeys = [],
  onConfirm, 
  onReject, 
  advanceToNextTopic,
  setMessages,
  messages = [],
  onSendText,
  setTitleKeys // Add setTitleKeys prop
}) => {
  const [titleData, setTitleData] = useState([]);
  const [selectedTitles, setSelectedTitles] = useState([]);
  
  useEffect(() => {
    // Process the titleKeys to create the bar chart data
    if (titleKeys && titleKeys.length > 0) {
      console.log('TitleDistribution: Using titleKeys with', titleKeys.length, 'titles');
      processTitleData(titleKeys);
      
      // Auto-select top 2 titles by default
      if (selectedTitles.length === 0) {
        const topTitles = titleKeys.slice(0, 2);
        setSelectedTitles(topTitles);
      }
    }
  }, [titleKeys]);
  
  /**
   * Process the titleKeys data to create the bar chart data
   * @param {Array} keys - The titleKeys array
   */
  const processTitleData = (keys) => {
    // Take the top titles (up to 5)
    const topTitles = keys.slice(0, 5).map(title => ({
      title,
      // We don't have actual counts, so we'll use the position in the array as a proxy for popularity
      // The first title is the most popular, so we'll give it a higher count
      count: Math.max(100 - (keys.indexOf(title) * 15), 10)
    }));
    
    // Calculate the total count
    const totalCount = topTitles.reduce((sum, item) => sum + item.count, 0);
    
    // Calculate percentages
    const titlesWithPercentages = topTitles.map(item => ({
      ...item,
      percentage: Math.round((item.count / totalCount) * 100)
    }));
    
    setTitleData(titlesWithPercentages);
  };
  
  /**
   * Handle title selection/deselection
   * @param {string} title - The title to toggle selection
   */
  const handleTitleSelection = (title) => {
    setSelectedTitles(prev => {
      if (prev.includes(title)) {
        return prev.filter(t => t !== title);
      } else {
        return [...prev, title];
      }
    });
  };
  
  /**
   * Handle confirm button click
   */
  const handleConfirm = () => {
    // Get the selected title data
    const selectedTitleData = titleData.filter(item => 
      selectedTitles.includes(item.title)
    );
    
    // Use onSendText to send a user message confirming the titles
    if (onSendText) {
      const selectedTitlesText = selectedTitleData.map(item => item.title).join(", ");
      onSendText(`I've selected these titles: ${selectedTitlesText}`);
    }
    
    // Call the onConfirm callback with selected titles
    if (onConfirm) {
      onConfirm(selectedTitleData);
    }
    
    // Advance to the next topic if available
    if (advanceToNextTopic) {
      setTimeout(() => {
        advanceToNextTopic();
      }, 100);
    }
  };
  
  /**
   * Handle direct input button click
   */
  const handleDirectInput = () => {
    // Add AI message asking for input
    if (setMessages) {
      const aiMessage = {
        role: 'AI',
        content: `I understand you'd like to specify titles directly. What kinds of titles are you looking for?`
      };
      
      // Update messages state with the AI message
      setMessages(prev => {
        const newMessages = [...prev, aiMessage];
        return newMessages;
      });
      
      // Reset titleKeys if setTitleKeys is provided
      if (setTitleKeys) {
        console.log('Resetting titleKeys in TitleDistribution component');
        setTitleKeys([]);
      }
      
      // Send a special message via onSendText that will be detected by topicDecisionEngine
      if (onSendText) {
        onSendText("I'll specify my own titles instead of using the suggested ones");
      }
      
      // Call the onReject callback
      if (onReject) {
        onReject();
      }
    }
  };
  
  // Render the component
  return (
    <Paper
      elevation={3}
      sx={{
        width: '100%',
        p: 4,
        borderRadius: 2,
        bgcolor: 'white',
        overflow: 'hidden'
      }}
    >
      <Typography variant="h6" gutterBottom fontWeight="bold" color="#333">
        {selectedTitles.length} titles selected
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Click on the titles below to select or deselect them. These are the most common job titles past candidates had before joining your company in a similar role.
      </Typography>
      
      {/* Title bars */}
      <Box sx={{ mb: 4 }}>
        {titleData.map((item, index) => {
          const isSelected = selectedTitles.includes(item.title);
          
          return (
            <Box 
              key={index} 
              sx={{ mb: 2 }}
              onClick={() => handleTitleSelection(item.title)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    height: 40,
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                >
                  {/* Title label */}
                  <Box
                    sx={{
                      bgcolor: isSelected ? '#74A9F8' : '#E0E0E0',
                      borderTopLeftRadius: 4,
                      borderBottomLeftRadius: 4,
                      py: 1,
                      px: 2,
                      width: '45%',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      ...(isSelected && {
                        borderTop: '2px solid #5A93E5',
                        borderLeft: '2px solid #5A93E5',
                        borderBottom: '2px solid #5A93E5',
                        borderRight: 'none',
                      })
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ 
                        textAlign: 'left', 
                        color: isSelected ? 'white' : '#333333'
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                  
                  {/* Percentage bar */}
                  <Box
                    sx={{
                      bgcolor: isSelected ? '#74A9F8' : '#E0E0E0',
                      height: 40,
                      width: `${Math.min(55, Math.max(item.percentage * 0.55, 5))}%`,
                      position: 'absolute',
                      left: '45%',
                      borderTopRightRadius: 4,
                      borderBottomRightRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 2,
                      ...(isSelected && {
                        borderTop: '2px solid #5A93E5',
                        borderRight: '2px solid #5A93E5',
                        borderBottom: '2px solid #5A93E5',
                        borderLeft: 'none'
                      })
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      fontWeight="medium" 
                      sx={{ 
                        color: isSelected ? 'white' : '#333333'
                      }}
                    >
                      {`${item.percentage}%`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      
      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedTitles.length === 0}
          sx={{
            bgcolor: '#74A9F8',
            '&:hover': { bgcolor: '#5A93E5' },
            color: 'white',
            px: 3,
            '&.Mui-disabled': {
              bgcolor: 'rgba(116, 169, 248, 0.3)',
              color: 'white'
            }
          }}
        >
          {selectedTitles.length === 0 
            ? 'Select at least one title' 
            : `Confirm Selection (${selectedTitles.length})`}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDirectInput}
          sx={{
            borderColor: '#F44336',
            color: '#F44336',
            '&:hover': {
              borderColor: '#D32F2F',
              bgcolor: 'rgba(244, 67, 54, 0.04)'
            },
            px: 3
          }}
        >
          None of the above
        </Button>
      </Box>
    </Paper>
  );
};

export default TitleDistribution;
